import { html, nothing } from "lit";
import type { CSSResultGroup } from "lit";

import { customElement, property, query } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { ifDefined } from "lit/directives/if-defined.js";

import { selectStyles } from "./select.css.ts";

import "../field-wrapper/field-wrapper.ts";
import { FormElement } from "../form-element.ts";
import { SlotContentController } from "ui-common/slot-content-controller";

function getId() {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  const length = 6;
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  for (let i = 0; i < length; i++) {
    result += chars[array[i]! % chars.length];
  }
  return result;
}

export interface SelectOption {
  value: string;
  label: string;
}

@customElement("wow-select")
export class WowSelect extends FormElement {
  static override styles: CSSResultGroup = [selectStyles];

  @query(".select") private selectElement!: HTMLSelectElement;

  @property({ type: String }) label = "";
  @property({ attribute: "help-text" }) helpText = "";
  @property({ attribute: "info-message" }) infoMessage = "";
  @property({ type: String }) placeholder = "";
  @property({ type: Array }) options: SelectOption[] = [];

  @property({ type: Boolean, reflect: true }) required = false;
  @property({ type: Boolean, reflect: true }) disabled = false;

  private prefixSlot = new SlotContentController(this, "prefix");

  override id = getId();

  public setError(message: string) {
    this._internals.setValidity({ customError: true }, message, this.selectElement);
  }

  public clearError() {
    this._internals.setValidity({});
  }

  public get select(): HTMLSelectElement {
    return this.selectElement;
  }

  private validateSelect(): void {
    if (!this.selectElement) return;

    const validity = this.selectElement.validity;
    const isValid = validity.valid && !this.customError;

    this.hasError = !isValid;

    if (!isValid) {
      this.errorMessage = this.customError || this.selectElement.validationMessage;
      this._internals.setValidity(this.customError ? { customError: true } : validity, this.errorMessage);
    } else {
      this.errorMessage = "";
      this._internals.setValidity({});
    }
  }

  private handleChange(e: Event) {
    this.value = (e.target as HTMLSelectElement).value;

    // Dispatch a custom change event that bubbles up
    this.dispatchEvent(
      new CustomEvent("change", {
        detail: { value: this.value },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleFocus(): void {
    this.focused = true;
  }

  private handleBlur(): void {
    this.focused = false;
    this.validateSelect();
  }

  private handleInvalid(event: Event): void {
    event.preventDefault();
    this.validateSelect();
  }

  override render() {
    const selectClasses = {
      select: true,
      "with-placeholder": !!this.placeholder,
      "with-prefix": this.prefixSlot.hasContent,
      invalid: this.validationMessage,
    };

    const placeholderClasses = {
      "select-placeholder": true,
      active: !!this.value || this.focused,
    };

    return html`
      <wow-field-wrapper>
        ${this.label
          ? html`<label slot="label" class="label" for=${ifDefined(this.id)}>${this.label}</label>`
          : nothing}
        ${this.helpText ? html`<span slot="help" class="help-text">${this.helpText}</span>` : nothing}

        <select
          id=${ifDefined(this.id)}
          slot="control"
          class=${classMap(selectClasses)}
          name=${ifDefined(this.name)}
          .value=${this.value}
          ?required=${this.required}
          ?disabled=${this.disabled}
          @change=${this.handleChange}
          @focus=${this.handleFocus}
          @blur=${this.handleBlur}
          @invalid=${this.handleInvalid}
          aria-label=${ifDefined(this.label || undefined)}
          aria-invalid=${this.hasError ? "true" : "false"}
          aria-describedby=${this.hasError ? "error-message" : undefined}
        >
          ${this.options.map(
            (option) =>
              html`<option value=${option.value} ?selected=${this.value === option.value}>${option.label}</option>`,
          )}
        </select>

        <span slot="control" class="select-prefix"><slot name="prefix"></slot></span>

        ${this.placeholder
          ? html`<label slot="control" class=${classMap(placeholderClasses)} for=${this.id}>${this.placeholder}</label>`
          : nothing}
        ${this.validationMessage
          ? html`<span class="error-message" slot="error">${this.validationMessage}</span>`
          : this.infoMessage
            ? html`<span class="info-message" slot="error">${this.infoMessage}</span>`
            : nothing}
      </wow-field-wrapper>
    `;
  }
}
